import { createContext, useContext, useMemo } from 'react';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';

export type ProjectSegmentOption = {
  identifier: string;
  name: string;
};

export type ProjectPatientJourneyOption = {
  id: number;
  title: string;
  publishState: string;
};

/**
 * Where the Segment / Patient Journey filters get their options. Apps whose project data is not
 * served by the chat API (the legacy frontend) provide their own; everyone else gets the chat API's.
 * Each fetcher returns only what the user may see (the API hides drafts they can't view).
 */
export type ProjectEntityFetchers = {
  fetchSegments: (projectId: number) => Promise<ProjectSegmentOption[]>;
  fetchPatientJourneys: (projectId: number) => Promise<ProjectPatientJourneyOption[]>;
};

export const ProjectEntityFetchersContext = createContext<ProjectEntityFetchers | null>(null);

export function useProjectEntityFetchers(): ProjectEntityFetchers {
  const provided = useContext(ProjectEntityFetchersContext);
  const apiClient = useChatApiClient();

  return useMemo<ProjectEntityFetchers>(
    () =>
      provided ?? {
        fetchSegments: async (projectId) => {
          const response = await apiClient.projects.fetchProjectSegments({ projectId });
          return response.items.map(({ segment }) => ({ identifier: segment.identifier, name: segment.name }));
        },
        fetchPatientJourneys: async (projectId) => {
          const response = await apiClient.projects.getPatientJourneys(projectId);
          return response.items.map(({ id, title, publishState }) => ({ id, title, publishState }));
        },
      },
    [provided, apiClient]
  );
}
